/**
 * SeniorSettingsPage - Global Settings Configuration for Seniors
 * 
 * Caregivers configure centralized settings that are consumed by the Device PWA.
 * Single source of truth is the backend (MongoDB).
 * 
 * Settings include:
 * - Language & Localization
 * - Audio (TTS voice, volume)
 * - UI Behavior (screensaver timeout, auto-answer)
 * - Content Preferences (default radio station)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Loader2, Volume2, Clock, Phone, Radio } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Available TTS voices
const TTS_VOICES = [
  { value: 'female_soft', label: 'Weiblich Sanft' },
  { value: 'female_standard', label: 'Weiblich Standard' },
  { value: 'male_soft', label: 'Männlich Sanft' },
  { value: 'male_standard', label: 'Männlich Standard' },
  { value: 'child_friendly', label: 'Kinderfreundlich' },
];

// Available languages
const LANGUAGES = [
  { value: 'de-DE', label: 'Deutsch' },
  { value: 'tr-TR', label: 'Türkisch' },
  { value: 'en-US', label: 'English' },
  { value: 'ar-SA', label: 'العربية (Arabic)' },
];

// Screensaver timeout options
const SCREENSAVER_TIMEOUTS = [
  { value: 30, label: '30 Sekunden' },
  { value: 60, label: '1 Minute' },
  { value: 120, label: '2 Minuten' },
  { value: 180, label: '3 Minuten' },
  { value: 300, label: '5 Minuten' },
  { value: 600, label: '10 Minuten' },
];

// Mock radio stations (should match radios.json)
const RADIO_STATIONS = [
  { value: null, label: 'Keine Vorauswahl' },
  { value: 'radyo_turku', label: 'Radyo Türkü' },
  { value: 'wdr2', label: 'WDR 2' },
  { value: 'bayern3', label: 'Bayern 3' },
  { value: 'antenne_bayern', label: 'Antenne Bayern' },
];

export default function SeniorSettingsPage() {
  const { seniorId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [senior, setSenior] = useState(null);

  // Settings state
  const [language, setLanguage] = useState('de-DE');
  const [ttsVoice, setTtsVoice] = useState('female_soft');
  const [defaultVolume, setDefaultVolume] = useState(60);
  const [screensaverTimeout, setScreensaverTimeout] = useState(120);
  const [autoAnswerCalls, setAutoAnswerCalls] = useState(false);
  const [defaultRadioStation, setDefaultRadioStation] = useState(null);

  useEffect(() => {
    if (seniorId) {
      loadData();
    }
  }, [seniorId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch senior profile
      const seniorRes = await axios.get(
        `${API}/api/seniors/${seniorId}`,
        { withCredentials: true }
      );
      setSenior(seniorRes.data);

      // Fetch settings
      const settingsRes = await axios.get(
        `${API}/api/seniors/${seniorId}/settings`,
        { withCredentials: true }
      );

      const settings = settingsRes.data;
      setLanguage(settings.language || 'de-DE');
      setTtsVoice(settings.tts_voice || 'female_soft');
      setDefaultVolume(settings.default_volume ?? 60);
      setScreensaverTimeout(settings.screensaver_timeout_seconds || 120);
      setAutoAnswerCalls(settings.auto_answer_calls || false);
      setDefaultRadioStation(settings.default_radio_station_key || null);

      console.log('[SeniorSettings] Loaded settings:', settings);
    } catch (error) {
      console.error('[SeniorSettings] Load error:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!seniorId) return;

    try {
      setSaving(true);

      const payload = {
        language,
        tts_voice: ttsVoice,
        default_volume: defaultVolume,
        screensaver_timeout_seconds: screensaverTimeout,
        auto_answer_calls: autoAnswerCalls,
        default_radio_station_key: defaultRadioStation,
      };

      await axios.put(
        `${API}/api/seniors/${seniorId}/settings`,
        payload,
        { withCredentials: true }
      );

      toast.success('Einstellungen erfolgreich gespeichert');
      console.log('[SeniorSettings] Saved:', payload);
    } catch (error) {
      console.error('[SeniorSettings] Save error:', error);
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Fehler beim Speichern: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Einstellungen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/family/seniors/${seniorId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Geräte-Einstellungen
              </h1>
              {senior && (
                <p className="text-lg text-gray-600">
                  Konfiguration für <strong>{senior.name}</strong>
                </p>
              )}
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Speichert...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Speichern</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex gap-3">
            <div className="text-blue-600 text-xl">ℹ️</div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Zentrale Einstellungen
              </h3>
              <p className="text-sm text-blue-800">
                Diese Einstellungen gelten für das Tablet/Gerät von {senior?.name || 'diesem Senior'}. 
                Änderungen werden beim nächsten App-Start auf dem Gerät übernommen.
              </p>
            </div>
          </div>
        </div>

        {/* Settings Form */}
        <div className="space-y-6">
          {/* Language & Localization */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              🌍 Sprache & Lokalisierung
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sprache
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  UI-Sprache für das Gerät
                </p>
              </div>
            </div>
          </div>

          {/* Audio Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Volume2 className="w-6 h-6" />
              Audio-Einstellungen
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TTS-Stimme
                </label>
                <select
                  value={ttsVoice}
                  onChange={(e) => setTtsVoice(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {TTS_VOICES.map((voice) => (
                    <option key={voice.value} value={voice.value}>
                      {voice.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Sprachstimme für Vorlesefunktionen
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standard-Lautstärke: {defaultVolume}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={defaultVolume}
                  onChange={(e) => setDefaultVolume(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Leise (0%)</span>
                  <span>Laut (100%)</span>
                </div>
              </div>
            </div>
          </div>

          {/* UI Behavior */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6" />
              Verhalten
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildschirmschoner Timeout
                </label>
                <select
                  value={screensaverTimeout}
                  onChange={(e) => setScreensaverTimeout(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {SCREENSAVER_TIMEOUTS.map((timeout) => (
                    <option key={timeout.value} value={timeout.value}>
                      {timeout.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Zeit bis zur Aktivierung des Bildschirmschoners
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">
                      Anrufe automatisch annehmen
                    </p>
                    <p className="text-xs text-gray-500">
                      Eingehende Anrufe werden automatisch angenommen
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setAutoAnswerCalls(!autoAnswerCalls)}
                  disabled={saving}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    autoAnswerCalls ? 'bg-violet-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      autoAnswerCalls ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Content Preferences */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Radio className="w-6 h-6" />
              Inhalts-Präferenzen
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standard-Radiosender
                </label>
                <select
                  value={defaultRadioStation || ''}
                  onChange={(e) => setDefaultRadioStation(e.target.value || null)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {RADIO_STATIONS.map((station) => (
                    <option key={station.value || 'none'} value={station.value || ''}>
                      {station.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Wird beim Öffnen der Radio-Funktion automatisch abgespielt
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Save Button (Mobile) */}
        <div className="fixed bottom-6 right-6 md:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-4 bg-violet-600 text-white rounded-full font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Speichern</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
